import React, { useEffect, useRef, useState } from 'react';
import QuoteDecimalInput from './QuoteDecimalInput';
import QuoteHelp from './QuoteHelp';
import {
  getQuoteCustomer,
  getQuoteCustomers,
  getQuoteCustomerTerms,
  proposeQuoteCustomerSnapshot,
} from '../quote.api';

const TextArea = ({ label, field, editor, onUpdate, rows = 3 }) => (
  <label>
    <span>{label}</span>
    <textarea rows={rows} disabled={!editor.permissions?.canEdit} value={editor[field] || ''} onChange={(event) => onUpdate(field, event.target.value)} />
  </label>
);

const QuoteInformationPanel = ({ editor, roundingModes, localOnly = false, onUpdate, onApplySnapshot, onApplyPartnerTerms }) => {
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [termsNotice, setTermsNotice] = useState('');
  const snapshot = editor.customerSnapshot || {};
  // Customer changes trigger this lookup; the current mode only guards it.
  // Changing mode alone must not reload contacts or replace local selections.
  const contactLookupMode = useRef(localOnly);
  contactLookupMode.current = localOnly;

  useEffect(() => {
    if (localOnly) return;
    getQuoteCustomers().then((rows) => setCustomers(Array.isArray(rows) ? rows : [])).catch(() => setCustomers([]));
  }, [localOnly]);

  useEffect(() => {
    if (contactLookupMode.current || !snapshot.customerCompanyId) return;
    getQuoteCustomer(snapshot.customerCompanyId)
      .then((customer) => setContacts(customer.contacts || customer.Contacts || []))
      .catch(() => setContacts([]));
  }, [snapshot.customerCompanyId]);

  const localCustomerSnapshot = (customer, contact = null) => ({
    customerCompanyId: customer.id,
    customerContactId: contact?.id || null,
    customerLegalName: customer.legalName || customer.socialReason || customer.name || '',
    customerTradeName: customer.name || customer.socialReason || '',
    customerAddressLine1: customer.addressLine1 || customer.address || '',
    customerAddressLine2: customer.addressLine2 || '',
    customerPostalCode: customer.postalCode || '',
    customerCity: customer.city || '',
    customerCountry: customer.countryCode || customer.country || '',
    customerRegistrationNumber: customer.siret || customer.siren || '',
    customerVatNumber: customer.vat || '',
    customerContactName: contact ? (contact.contactName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim()) : '',
    customerContactEmail: contact?.email || '',
    customerContactPhone: contact?.phone || contact?.mobile || '',
  });

  const selectCustomer = async (companyId) => {
    onUpdate('companyId', companyId || '');
    if (!companyId) {
      setContacts([]);
      onApplySnapshot({ customerCompanyId: null, customerContactId: null });
      return;
    }
    const customer = await getQuoteCustomer(companyId);
    setContacts(customer.contacts || customer.Contacts || []);
    if (editor.quoteVersionId) {
      onApplySnapshot(await proposeQuoteCustomerSnapshot(editor.quoteVersionId, companyId));
    } else {
      onApplySnapshot(localCustomerSnapshot(customer));
    }
    try {
      const terms = await getQuoteCustomerTerms(companyId);
      if (terms?.enabled !== false) {
        onApplyPartnerTerms?.(companyId, terms);
        setTermsNotice('Conditions commerciales du partenaire appliquées à cette version.');
      } else {
        setTermsNotice('Aucune condition commerciale automatique active pour ce partenaire.');
      }
    } catch {
      setTermsNotice('Client sélectionné. Les conditions commerciales spécifiques n’ont pas pu être chargées.');
    }
  };

  const selectContact = async (contactId) => {
    if (!snapshot.customerCompanyId) return;
    if (editor.quoteVersionId) {
      onApplySnapshot(await proposeQuoteCustomerSnapshot(editor.quoteVersionId, snapshot.customerCompanyId, contactId));
      return;
    }
    const customer = await getQuoteCustomer(snapshot.customerCompanyId);
    const allContacts = customer.contacts || customer.Contacts || [];
    const contact = allContacts.find((item) => String(item.id) === String(contactId)) || null;
    onApplySnapshot(localCustomerSnapshot(customer, contact));
  };

  return (
    <section className={`quote-panel quote-information-form ${!editor.permissions?.canEdit ? 'quote-panel--readonly' : ''}`}>
      <header>
        <div><h2>Informations du document <QuoteHelp title="Données de la version">{`Ces données appartiennent exclusivement à la version V${editor.versionNumber}.`}</QuoteHelp></h2></div>
        <span className="quote-version-copy-badge">Copie indépendante du CRM</span>
      </header>

      <div className="quote-information-form__document">
        <TextArea label="Objet" field="objectText" editor={editor} onUpdate={onUpdate} />
        <TextArea label="Périmètre" field="scopeText" editor={editor} onUpdate={onUpdate} />
        <TextArea label="Exclusions" field="exclusionsText" editor={editor} onUpdate={onUpdate} />
        <TextArea label="Notes destinées au client" field="notesClient" editor={editor} onUpdate={onUpdate} />
        <TextArea label="Notes internes" field="notesInternal" editor={editor} onUpdate={onUpdate} />
      </div>

      <div className="quote-information-form__snapshot">
        <h3>Client et contact capturés</h3>
        {termsNotice && <p className="quote-partner-terms-notice">{termsNotice}</p>}
        <div className="quote-form-grid">
          <label><span>Proposer depuis le CRM</span><select disabled={!editor.permissions?.canEdit} value={snapshot.customerCompanyId || ''} onChange={(event) => selectCustomer(event.target.value)}><option value="">Sélectionner un client</option>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.socialReason || customer.legalName || customer.name}</option>)}</select></label>
          <label><span>Contact CRM</span><select disabled={!editor.permissions?.canEdit || contacts.length === 0} value={snapshot.customerContactId || ''} onChange={(event) => selectContact(event.target.value)}><option value="">Aucun contact</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.contactName || `${contact.firstName || ''} ${contact.lastName || ''}`}</option>)}</select></label>
          <label><span>Raison sociale utilisée</span><input disabled={!editor.permissions?.canEdit} value={snapshot.customerLegalName || ''} onChange={(event) => onApplySnapshot({ customerLegalName: event.target.value })} /></label>
          <label><span>Nom du contact utilisé</span><input disabled={!editor.permissions?.canEdit} value={snapshot.customerContactName || ''} onChange={(event) => onApplySnapshot({ customerContactName: event.target.value })} /></label>
          <label className="quote-form-grid__wide"><span>Adresse ligne 1</span><input disabled={!editor.permissions?.canEdit} value={snapshot.customerAddressLine1 || ''} onChange={(event) => onApplySnapshot({ customerAddressLine1: event.target.value })} /></label>
          <label className="quote-form-grid__wide"><span>Adresse ligne 2</span><input disabled={!editor.permissions?.canEdit} value={snapshot.customerAddressLine2 || ''} onChange={(event) => onApplySnapshot({ customerAddressLine2: event.target.value })} /></label>
          <label><span>Code postal</span><input disabled={!editor.permissions?.canEdit} value={snapshot.customerPostalCode || ''} onChange={(event) => onApplySnapshot({ customerPostalCode: event.target.value })} /></label>
          <label><span>Ville</span><input disabled={!editor.permissions?.canEdit} value={snapshot.customerCity || ''} onChange={(event) => onApplySnapshot({ customerCity: event.target.value })} /></label>
          <label><span>Email du contact</span><input type="email" disabled={!editor.permissions?.canEdit} value={snapshot.customerContactEmail || ''} onChange={(event) => onApplySnapshot({ customerContactEmail: event.target.value })} /></label>
          <label><span>Téléphone du contact</span><input disabled={!editor.permissions?.canEdit} value={snapshot.customerContactPhone || ''} onChange={(event) => onApplySnapshot({ customerContactPhone: event.target.value })} /></label>
        </div>
      </div>

      <div className="quote-information-form__terms">
        <h3>Conditions documentaires</h3>
        <div className="quote-form-grid">
          <label className="quote-form-grid__wide"><span>Conditions de paiement</span><textarea rows="2" disabled={!editor.permissions?.canEdit} value={editor.paymentTerms || ''} onChange={(event) => onUpdate('paymentTerms', event.target.value)} /></label>
          <label><span>Délai de paiement (jours)</span><input type="number" min="0" disabled={!editor.permissions?.canEdit} value={editor.paymentDelayDays ?? ''} onChange={(event) => onUpdate('paymentDelayDays', event.target.value)} /></label>
          <label><span>Acompte (%)</span><QuoteDecimalInput min={0} max={100} disabled={!editor.permissions?.canEdit} value={editor.depositRate ?? 0} onChange={(value) => onUpdate('depositRate', value)} /></label>
          <label><span>Remise globale</span><div className="quote-inline-fields"><QuoteDecimalInput min={0} disabled={!editor.permissions?.canEdit} value={editor.globalDiscountValue ?? 0} onChange={(value) => onUpdate('globalDiscountValue', value)} /><select disabled={!editor.permissions?.canEdit} value={editor.globalDiscountKind || 'percent'} onChange={(event) => onUpdate('globalDiscountKind', event.target.value)}><option value="percent">%</option><option value="amount">€</option></select></div></label>
          <label><span>Validité du devis (jours)</span><input type="number" min="0" step="1" disabled={!editor.permissions?.canEdit} value={editor.validityDays ?? 30} onChange={(event) => onUpdate('validityDays', event.target.value)} /><small>La validité commence à la validation du devis.</small></label>
          <label><span>Valable jusqu’au</span><input type="text" disabled readOnly value={editor.versionStatus === 'draft' ? 'Calculée à la validation' : editor.validUntil ? editor.validUntil.split('-').reverse().join('/') : 'Non renseignée'} /><small>Date calculée automatiquement selon la durée de validité.</small></label>
          <label><span>Délai d’exécution</span><input disabled={!editor.permissions?.canEdit} value={editor.executionDelay || ''} onChange={(event) => onUpdate('executionDelay', event.target.value)} /></label>
          <label><span>TVA par défaut (%)</span><QuoteDecimalInput min={0} disabled={!editor.permissions?.canEdit} value={editor.defaultVatRate} onChange={(value) => onUpdate('defaultVatRate', value)} /></label>
          <label><span>Devise</span><input disabled readOnly value={editor.currencyCode ?? 'EUR'} aria-label="Devise" /></label>
          <label><span>Arrondi</span><select disabled={!editor.permissions?.canEdit} value={editor.roundingMode} onChange={(event) => onUpdate('roundingMode', event.target.value)}>{!roundingModes.some((mode) => mode.value === editor.roundingMode) && <option value={editor.roundingMode} disabled>{editor.roundingMode} (historique incompatible)</option>}{roundingModes.map((mode) => <option key={mode.value} value={mode.value} disabled={mode.disabled}>{mode.label}</option>)}</select></label>
        </div>
      </div>
    </section>
  );
};

export default QuoteInformationPanel;
